import React, { useState } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import CurrencyInput from '../../../../molecules/CurrencyInput';
import TextEdit from '../../../../molecules/TextEdit';
import SelectTextField from '../../../../molecules/SelectTextField';
import { FaTimes, FaPlus, FaFilePdf, FaSearch, FaEraser } from 'react-icons/fa';
import { ContractAsReceivableItem, DraweeDocumentType } from '../../types/operations';
import { PersonSearchResult, ApiListResponse } from '../../types/api-responses';
import { personApi } from '../../../../../services/personApi';
import { applyDateMask } from '../../../../../utils/dateMask';
import appConfig from '../../../../../config/app-config';

const { address: PERSON_SERVICE_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

const MIN_ROWS = 5;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

const DOCUMENT_TYPE_OPTIONS = [
    { value: DraweeDocumentType.CPF, label: 'CPF' },
    { value: DraweeDocumentType.CNPJ, label: 'CNPJ' },
];

export interface ContractsAsReceivablesData {
    items: ContractAsReceivableItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

interface ContractsAsReceivablesManagerProps {
    value: ContractsAsReceivablesData;
    onChange: (data: ContractsAsReceivablesData) => void;
    onFileDownload?: (fileName: string) => void;
    disabled?: boolean;
    errorFields?: Array<{ fieldName: string; message: string }>;
}

interface DraweeDraft {
    person_id: string;
    document_type: DraweeDocumentType;
    document: string;
    name: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    zip_code: string;
    ibge_code: string;
}

const EMPTY_DRAWEE: DraweeDraft = {
    person_id: '',
    document_type: DraweeDocumentType.CNPJ,
    document: '',
    name: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
    zip_code: '',
    ibge_code: '',
};

const generateId = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const formatCurrency = (val: number): string =>
    (val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const resolveDocumentType = (person: PersonSearchResult): { document: string; type: DraweeDocumentType } => {
    const cnpjDoc = person.documents?.find((doc) => doc.type === 'CNPJ');
    const cpfDoc = person.documents?.find((doc) => doc.type === 'CPF');
    if (cnpjDoc) return { document: cnpjDoc.number, type: DraweeDocumentType.CNPJ };
    if (cpfDoc) return { document: cpfDoc.number, type: DraweeDocumentType.CPF };
    return { document: '', type: DraweeDocumentType.CNPJ };
};

const ContractsAsReceivablesManager: React.FC<ContractsAsReceivablesManagerProps> = ({
    value,
    onChange,
    onFileDownload,
    disabled,
}) => {
    const newContractValue = useSignal(0);
    const newContractIssueDate = useSignal('');
    const newContractNumber = useSignal('');
    const newDescription = useSignal('');
    const newFile = useSignal<File | null>(null);

    // Sacado draft
    const [drawee, setDrawee] = useState<DraweeDraft>(EMPTY_DRAWEE);
    const [loadingDrawee, setLoadingDrawee] = useState(false);
    // Dados do título (recebível)
    const newDocumentTitle = useSignal('');
    const newTitleInstallment = useSignal('');
    const newIssueDate = useSignal('');
    const newOperationDate = useSignal('');
    const newPresentValue = useSignal(0);
    const newDueDate = useSignal('');
    const newReceivableAmount = useSignal(0);

    // Sacado search
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<PersonSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    const runSearch = (query: string) => {
        setSearchValue(query);
        if (debounceTimer) clearTimeout(debounceTimer);
        if (query.length < MIN_SEARCH_LENGTH) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        const timer = setTimeout(async () => {
            try {
                const token = await Auth.currentSession().then((s) => s.getIdToken().getJwtToken());
                const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${query}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': PERSON_API_KEY || '' },
                });
                if (response.ok) {
                    const data: ApiListResponse<PersonSearchResult> = await response.json();
                    setSearchResults(data.results || []);
                }
            } catch (error: unknown) {
                void error;
            } finally {
                setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        setDebounceTimer(timer);
    };

    const handleSelectPerson = async (person: PersonSearchResult) => {
        const { document, type } = resolveDocumentType(person);
        setDrawee({
            ...EMPTY_DRAWEE,
            person_id: person.uuid,
            document_type: type,
            document,
            name: person.name || '',
        });
        setSearchOpen(false);
        setSearchValue('');
        setSearchResults([]);

        // Trazer endereço completo do person (com fallback no geolocation)
        setLoadingDrawee(true);
        try {
            const address = await personApi.getPersonFullAddress(person.uuid);
            if (address) {
                setDrawee((prev) => ({
                    ...prev,
                    street: address.street,
                    number: address.number,
                    complement: address.complement,
                    district: address.district,
                    city: address.city,
                    state: address.state,
                    zip_code: address.zip_code,
                    ibge_code: address.ibge_code,
                }));
            }
        } catch (error: unknown) {
            void error;
        } finally {
            setLoadingDrawee(false);
        }
    };

    // Limpa apenas os campos da parcela/título, mantendo contrato e contratante
    // para facilitar a inclusão de várias parcelas do mesmo contrato.
    const resetTitleFields = () => {
        newDocumentTitle.value = '';
        newTitleInstallment.value = '';
        newIssueDate.value = '';
        newOperationDate.value = '';
        newPresentValue.value = 0;
        newDueDate.value = '';
        newReceivableAmount.value = 0;
    };

    // Limpa todos os campos do draft (contrato + contratante + título).
    const resetAll = () => {
        newContractValue.value = 0;
        newContractIssueDate.value = '';
        newContractNumber.value = '';
        newDescription.value = '';
        newFile.value = null;
        setDrawee(EMPTY_DRAWEE);
        resetTitleFields();
    };

    const handleAdd = () => {
        const missing: string[] = [];
        if (!newContractNumber.value.trim()) missing.push('Nº Contrato');
        if (newContractValue.value <= 0) missing.push('Valor Contrato');
        if (!newContractIssueDate.value.trim()) missing.push('Data Emissão do Contrato');
        if (!newFile.value) missing.push('Contrato (PDF)');
        if (!drawee.document_type) missing.push('Tipo Documento');
        if (!drawee.document.trim()) missing.push('Documento');
        if (!drawee.name.trim()) missing.push('Nome');
        if (!drawee.street.trim()) missing.push('Logradouro');
        if (!drawee.number.trim()) missing.push('Número');
        if (!drawee.district.trim()) missing.push('Bairro');
        if (!drawee.city.trim()) missing.push('Cidade');
        if (!drawee.state.trim()) missing.push('UF');
        if (!drawee.zip_code.trim()) missing.push('CEP');
        if (!newDocumentTitle.value.trim()) missing.push('Nº Título');
        if (!newTitleInstallment.value.trim()) missing.push('Parcela do Título');
        if (!newIssueDate.value.trim()) missing.push('Data Emissão');
        if (!newOperationDate.value.trim()) missing.push('Data Operação Agrolend');
        if (newPresentValue.value <= 0) missing.push('Valor Presente');
        if (!newDueDate.value.trim()) missing.push('Data Vencimento');
        if (newReceivableAmount.value <= 0) missing.push('Valor do Título');

        if (missing.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${missing.join(', ')}`);
            return;
        }

        const id = generateId();
        const newItem: ContractAsReceivableItem = {
            receivable_contract_id: id,
            contract_number: newContractNumber.value || '',
            contract_value: newContractValue.value,
            contract_issue_date: newContractIssueDate.value || '',
            file_name: newFile.value?.name || '',
            description: newDescription.value || '',
            drawee_person_id: drawee.person_id || undefined,
            drawee_document_type: drawee.document_type,
            drawee_document: drawee.document,
            drawee_name: drawee.name,
            drawee_address_street: drawee.street,
            drawee_address_number: drawee.number,
            drawee_address_complement: drawee.complement,
            drawee_address_district: drawee.district,
            drawee_city: drawee.city,
            drawee_state: drawee.state,
            drawee_address_zip_code: drawee.zip_code,
            drawee_address_ibge_code: drawee.ibge_code,
            document_title: newDocumentTitle.value || '',
            title_installment: newTitleInstallment.value || '',
            issue_date: newIssueDate.value || '',
            operation_date: newOperationDate.value || '',
            present_value: newPresentValue.value || 0,
            due_date: newDueDate.value || '',
            receivable_amount: newReceivableAmount.value || 0,
        };

        const newFiles = new Map(value.files);
        if (newFile.value) {
            newFiles.set(id, newFile.value);
        }

        onChange({
            items: [...value.items, newItem],
            files: newFiles,
            existingFiles: value.existingFiles,
        });

        // Mantém contrato e contratante; limpa apenas os dados da parcela/título.
        resetTitleFields();
    };

    const handleRemove = (index: number) => {
        const item = value.items[index];
        const updatedFiles = new Map(value.files);
        const updatedExisting = new Map(value.existingFiles);
        updatedFiles.delete(item.receivable_contract_id);
        updatedExisting.delete(item.receivable_contract_id);

        onChange({
            items: value.items.filter((_, i) => i !== index),
            files: updatedFiles,
            existingFiles: updatedExisting,
        });
    };

    const handleFileChangeForItem = (receivableContractId: string, file: File | null) => {
        const updatedFiles = new Map(value.files);
        const updatedExisting = new Map(value.existingFiles);

        if (file) {
            updatedFiles.set(receivableContractId, file);
            updatedExisting.delete(receivableContractId);
        } else {
            updatedFiles.delete(receivableContractId);
        }

        const updatedItems = value.items.map(item =>
            item.receivable_contract_id === receivableContractId
                ? { ...item, file_name: file?.name || '' }
                : item
        );

        onChange({ items: updatedItems, files: updatedFiles, existingFiles: updatedExisting });
    };

    const handleRemoveExistingFile = (receivableContractId: string) => {
        const updatedExisting = new Map(value.existingFiles);
        updatedExisting.delete(receivableContractId);

        const updatedItems = value.items.map(item =>
            item.receivable_contract_id === receivableContractId
                ? { ...item, file_name: '' }
                : item
        );

        onChange({ items: updatedItems, files: value.files, existingFiles: updatedExisting });
    };

    const emptyRowCount = Math.max(0, MIN_ROWS - value.items.length);

    return (
        <div className="space-y-4">
            {/* Add form */}
            <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 space-y-4">
                {/* Dados do contrato */}
                <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-2">
                        <TextEdit
                            id="car_contract_number"
                            label="Nº Contrato *"
                            value={newContractNumber.value}
                            onChange={(e) => { newContractNumber.value = (e.target as HTMLInputElement).value; }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-span-2">
                        <CurrencyInput
                            id="car_contract_value"
                            label="Valor Contrato *"
                            prefix="R$"
                            fractionDigits={2}
                            value={newContractValue.value}
                            onChange={(v) => { newContractValue.value = v; }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-span-2">
                        <TextEdit
                            id="car_contract_issue_date"
                            label="Data Emissão *"
                            value={newContractIssueDate.value}
                            placeholder="dd/mm/aaaa"
                            onChange={(e) => { newContractIssueDate.value = applyDateMask((e.target as HTMLInputElement).value); }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-span-3">
                        <TextEdit
                            id="car_description"
                            label="Descrição"
                            value={newDescription.value}
                            onChange={(e) => { newDescription.value = (e.target as HTMLInputElement).value; }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Contrato (PDF) *
                        </label>
                        {newFile.value ? (
                            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-2 py-1.5 rounded border border-green-200 dark:border-green-700">
                                <FaFilePdf size={14} />
                                <span className="truncate flex-1">{newFile.value.name}</span>
                                <button type="button" onClick={() => { newFile.value = null; }} className="text-red-500 hover:text-red-700">
                                    <FaTimes size={12} />
                                </button>
                            </div>
                        ) : (
                            <label
                                htmlFor="car-new-file-upload"
                                className="flex items-center justify-center h-9 border-2 border-dashed border-gray-300 dark:border-gray-500 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                                <span className="text-xs text-gray-500 dark:text-gray-400">Selecionar PDF</span>
                                <input
                                    id="car-new-file-upload"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,application/pdf"
                                    disabled={disabled}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) newFile.value = file;
                                        e.target.value = '';
                                    }}
                                />
                            </label>
                        )}
                    </div>
                </div>

                {/* Dados do contratante (sacado) */}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Contratante</h4>
                        <button type="button"
                            onClick={() => setSearchOpen(true)}
                            disabled={disabled}
                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 dark:bg-gray-500 text-white text-xs rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaSearch size={11} /> Buscar Contratante
                        </button>
                    </div>

                    {/* Identificação */}
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-2">
                            <SelectTextField
                                id="car_drawee_document_type"
                                label="Tipo Documento *"
                                items={DOCUMENT_TYPE_OPTIONS}
                                value={drawee.document_type}
                                onChange={(e) => setDrawee({ ...drawee, document_type: e.target.value as DraweeDocumentType })}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-3">
                            <TextEdit
                                id="car_drawee_document"
                                label="Documento *"
                                value={drawee.document}
                                onChange={(e) => setDrawee({ ...drawee, document: (e.target as HTMLInputElement).value })}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-7">
                            <TextEdit
                                id="car_drawee_name"
                                label="Nome *"
                                value={loadingDrawee ? 'Carregando...' : drawee.name}
                                readOnly
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                    </div>

                    {/* Endereço */}
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-3 mb-1">Endereço</p>
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-3">
                            <TextEdit
                                id="car_drawee_zip_code"
                                label="CEP *"
                                value={loadingDrawee ? '...' : drawee.zip_code}
                                onChange={(e) => setDrawee({ ...drawee, zip_code: (e.target as HTMLInputElement).value })}
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                        <div className="col-span-7">
                            <TextEdit
                                id="car_drawee_street"
                                label="Logradouro *"
                                value={loadingDrawee ? 'Carregando...' : drawee.street}
                                onChange={(e) => setDrawee({ ...drawee, street: (e.target as HTMLInputElement).value })}
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="car_drawee_number"
                                label="Número *"
                                value={loadingDrawee ? '...' : drawee.number}
                                onChange={(e) => setDrawee({ ...drawee, number: (e.target as HTMLInputElement).value })}
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                        <div className="col-span-4">
                            <TextEdit
                                id="car_drawee_complement"
                                label="Complemento"
                                value={loadingDrawee ? '...' : drawee.complement}
                                onChange={(e) => setDrawee({ ...drawee, complement: (e.target as HTMLInputElement).value })}
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                        <div className="col-span-4">
                            <TextEdit
                                id="car_drawee_district"
                                label="Bairro *"
                                value={loadingDrawee ? 'Carregando...' : drawee.district}
                                onChange={(e) => setDrawee({ ...drawee, district: (e.target as HTMLInputElement).value })}
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                        <div className="col-span-3">
                            <TextEdit
                                id="car_drawee_city"
                                label="Cidade *"
                                value={loadingDrawee ? 'Carregando...' : drawee.city}
                                readOnly
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                        <div className="col-span-1">
                            <TextEdit
                                id="car_drawee_state"
                                label="UF *"
                                value={loadingDrawee ? '...' : drawee.state}
                                maxLength={2}
                                readOnly
                                disabled={disabled || loadingDrawee}
                            />
                        </div>
                    </div>
                </div>

                {/* Dados da parcela (título) */}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Dados da Parcela</h4>
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-2">
                            <TextEdit
                                id="car_document_title"
                                label="Nº Título *"
                                value={newDocumentTitle.value}
                                onChange={(e) => { newDocumentTitle.value = (e.target as HTMLInputElement).value; }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="car_title_installment"
                                label="Parcela do Título *"
                                value={newTitleInstallment.value}
                                onChange={(e) => { newTitleInstallment.value = (e.target as HTMLInputElement).value; }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="car_issue_date"
                                label="Data Emissão *"
                                value={newIssueDate.value}
                                onChange={(e) => { newIssueDate.value = applyDateMask((e.target as HTMLInputElement).value); }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="car_operation_date"
                                label="Data Operação Agrolend *"
                                value={newOperationDate.value}
                                onChange={(e) => { newOperationDate.value = applyDateMask((e.target as HTMLInputElement).value); }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="car_present_value"
                                label="Valor Presente *"
                                prefix="R$"
                                fractionDigits={2}
                                value={newPresentValue.value}
                                onChange={(v) => { newPresentValue.value = v; }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="car_drawee_due_date"
                                label="Data Vencimento *"
                                value={newDueDate.value}
                                onChange={(e) => { newDueDate.value = applyDateMask((e.target as HTMLInputElement).value); }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="car_receivable_amount"
                                label="Valor do Título *"
                                prefix="R$"
                                fractionDigits={2}
                                value={newReceivableAmount.value}
                                onChange={(v) => { newReceivableAmount.value = v; }}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-span-10 flex items-end justify-end gap-2">
                            <button type="button"
                                onClick={resetAll}
                                disabled={disabled || loadingDrawee}
                                className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 text-sm rounded hover:bg-gray-300 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Limpar dados do contrato e do contratante"
                            >
                                <FaEraser size={12} /> Limpar
                            </button>
                            <button type="button"
                                onClick={handleAdd}
                                disabled={disabled || loadingDrawee}
                                className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-600 dark:bg-gray-500 text-white text-sm rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FaPlus size={12} /> Adicionar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sacado search modal */}
            {searchOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSearchOpen(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-3/4 max-w-4xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold dark:text-white">Buscar Contratante</h3>
                            <button type="button" onClick={() => setSearchOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <FaTimes />
                            </button>
                        </div>
                        <div className="mb-4">
                            <TextEdit
                                label="CPF/CNPJ ou Nome"
                                value={searchValue}
                                onChange={(e) => runSearch((e.target as HTMLInputElement).value)}
                                placeholder=""
                            />
                        </div>
                        <div className="overflow-x-auto h-80">
                            <table className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Documento</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600 w-full">Nome</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ação</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                    {isSearching ? (
                                        <tr>
                                            <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Buscando...</td>
                                        </tr>
                                    ) : (
                                        searchResults.slice(0, 6).map((result, index) => {
                                            const { document } = resolveDocumentType(result);
                                            return (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 h-12">
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm dark:text-gray-300">{document || '-'}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm dark:text-gray-300 truncate">{result.name}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                        <button type="button"
                                                            onClick={() => handleSelectPerson(result)}
                                                            className="px-3 py-1 bg-gray-600 dark:bg-gray-500 text-white text-xs rounded hover:bg-gray-700 dark:hover:bg-gray-600"
                                                        >
                                                            Selecionar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Table with min 5 rows + zebra */}
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                        <tr>
                            <th className="px-3 py-2">Nº Contrato</th>
                            <th className="px-3 py-2">Valor Contrato</th>
                            <th className="px-3 py-2">Dt. Emissão Contrato</th>
                            <th className="px-3 py-2">Contratante</th>
                            <th className="px-3 py-2">Documento</th>
                            <th className="px-3 py-2">Endereço</th>
                            <th className="px-3 py-2">Cidade/UF</th>
                            <th className="px-3 py-2">CEP</th>
                            <th className="px-3 py-2">Nº Título</th>
                            <th className="px-3 py-2">Parcela</th>
                            <th className="px-3 py-2">Dt. Emissão</th>
                            <th className="px-3 py-2">Dt. Operação</th>
                            <th className="px-3 py-2">Valor Presente</th>
                            <th className="px-3 py-2">Dt. Vencimento</th>
                            <th className="px-3 py-2">Valor Título</th>
                            <th className="px-3 py-2">Arquivo</th>
                            <th className="px-3 py-2 w-16">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {value.items.map((item, index) => {
                            const existingFile = value.existingFiles.get(item.receivable_contract_id);
                            const uploadedFile = value.files.get(item.receivable_contract_id);
                            const fileName = uploadedFile?.name || existingFile || item.file_name || '';

                            return (
                                <tr key={item.receivable_contract_id} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                    <td className="px-3 py-2">{item.contract_number || '-'}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.contract_value)}</td>
                                    <td className="px-3 py-2">{item.contract_issue_date || '-'}</td>
                                    <td className="px-3 py-2">{item.drawee_name || '-'}</td>
                                    <td className="px-3 py-2">{item.drawee_document || '-'}</td>
                                    <td className="px-3 py-2">{item.drawee_address_street ? `${item.drawee_address_street}, ${item.drawee_address_number || 's/n'}${item.drawee_address_district ? ` - ${item.drawee_address_district}` : ''}` : '-'}</td>
                                    <td className="px-3 py-2">{item.drawee_city ? `${item.drawee_city}/${item.drawee_state || '-'}` : '-'}</td>
                                    <td className="px-3 py-2">{item.drawee_address_zip_code || '-'}</td>
                                    <td className="px-3 py-2">{item.document_title || '-'}</td>
                                    <td className="px-3 py-2">{item.title_installment || '-'}</td>
                                    <td className="px-3 py-2">{item.issue_date || '-'}</td>
                                    <td className="px-3 py-2">{item.operation_date || '-'}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.present_value || 0)}</td>
                                    <td className="px-3 py-2">{item.due_date || '-'}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.receivable_amount || 0)}</td>
                                    <td className="px-3 py-2">
                                        {fileName ? (
                                            <div className="flex items-center gap-2">
                                                <FaFilePdf className="text-red-500" size={12} />
                                                {existingFile && !uploadedFile ? (
                                                    <button type="button"
                                                        onClick={() => onFileDownload?.(existingFile)}
                                                        className="text-blue-600 dark:text-blue-400 underline text-xs hover:text-blue-800 bg-transparent border-0 p-0"
                                                    >
                                                        {existingFile}
                                                    </button>
                                                ) : (
                                                    <span className="text-xs truncate max-w-[120px]">{fileName}</span>
                                                )}
                                                {!disabled && (
                                                    <button type="button"
                                                        onClick={() => existingFile ? handleRemoveExistingFile(item.receivable_contract_id) : handleFileChangeForItem(item.receivable_contract_id, null)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <FaTimes size={10} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <label htmlFor={`car-file-${item.receivable_contract_id}`} className="text-xs text-blue-600 dark:text-blue-400 underline cursor-pointer hover:text-blue-800">
                                                Upload PDF
                                                <input
                                                    id={`car-file-${item.receivable_contract_id}`}
                                                    type="file"
                                                    className="hidden"
                                                    accept=".pdf,application/pdf"
                                                    disabled={disabled}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleFileChangeForItem(item.receivable_contract_id, file);
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <button type="button" onClick={() => handleRemove(index)} disabled={disabled} className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50" title="Remover">
                                            <FaTimes size={14} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`empty-${i}`} className={`${(value.items.length + i) % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2" colSpan={17}>&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ContractsAsReceivablesManager;
